import React, { useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useRowsLand } from './Fluid'; // [FLUID-4 2026-10-12] [FLUID-5 2026-10-12]

/*
  Shared skeleton-loading primitives.

  Why skeletons (not a spinner / "Loading…" text): every data view in this app
  has a known, fixed shape (a table, a KPI bar). A skeleton holds that exact
  shape while data is in flight, so when the data lands it just "fills in" with
  no layout jump — which reads as markedly faster and more premium than a
  collapsing loader. Spinners are kept only for short button actions
  (Save / Refresh), never for content areas.

  Performance: the shimmer is a single transform-translated gradient overlay
  (GPU-composited), not an animated background-position, so painting dozens of
  bars stays cheap. Honours prefers-reduced-motion.
*/

// One left-to-right sweep of the highlight band across a bar.
const shimmer = keyframes`
  100% { transform: translateX(100%); }
`;

// A single shimmering placeholder bar. Width/height/radius are configurable so
// the same primitive builds table cells, KPI values, labels, headings, etc.
export const SkeletonBox = styled.div<{
  $w?: string;
  $h?: string;
  $radius?: string;
  $align?: 'left' | 'right' | 'center';
  $mt?: string;
}>`
  position: relative;
  overflow: hidden;
  width: ${(p) => p.$w || '100%'};
  height: ${(p) => p.$h || '12px'};
  border-radius: ${(p) => p.$radius || '7px'};
  margin-top: ${(p) => p.$mt || '0'};
  /* Centre/right alignment inside its cell (numeric columns sit right). */
  margin-left: ${(p) => (p.$align === 'right' ? 'auto' : p.$align === 'center' ? 'auto' : '0')};
  margin-right: ${(p) => (p.$align === 'center' ? 'auto' : '0')};
  background: ${(p) =>
    p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(22,25,23,0.06)'};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)')} 50%,
      transparent 100%
    );
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

/* ─────────────────────────  Table skeleton  ───────────────────────── */

// Self-contained table chrome that mirrors DataTable's look (sticky pill
// header, line-coloured row borders, 13px / 8-12px cell rhythm) so the
// skeleton lines up with the real table that replaces it.
const SkWrap = styled.div`
  overflow-x: hidden;
  /* Gentle fade-in so the skeleton itself doesn't pop in abruptly. */
  animation: skFade 0.18s ease both;
  @keyframes skFade {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* [MOBILE 2026-07-07] On phones the real tables scroll horizontally inside an
     overflow-x:auto wrapper (see DataTable's TableWrapper and each bespoke page's
     TableScroll). The skeleton must do the SAME, or its fixed 100% layout crushes
     every column into single-letter ellipsis headers + dots (looks broken during a
     tab switch). Below the mobile breakpoint we own the horizontal scroll with the
     same touch polish the real wrapper uses, so the skeleton reads as the table
     that's coming and fills in without a jump. Desktop is untouched. */
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
  }
`;

const SkTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  /* [MOBILE 2026-07-07] Drop the fixed 100% layout that crushes columns on phones;
     grow to content and let SkWrap scroll — exactly like the real table
     (width:max-content; min-width:100%). Per-column min-widths on the cells below
     give it a readable, scrollable shape instead of a squeezed grid. */
  @media (max-width: 768px) {
    width: max-content;
    min-width: 100%;
    table-layout: auto;
  }
`;

const SkTh = styled.th<{ $align?: string }>`
  background: ${(p) => p.theme.pill};
  padding: 10px 12px;
  text-align: ${(p) => p.$align || 'left'};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* [MOBILE 2026-07-07] With the auto layout above, let each header show in full
     (it defines the column's natural width, matching the real header which is also
     nowrap) and floor the column so short-labelled ones stay readable, not skinny. */
  @media (max-width: 768px) {
    overflow: visible;
    text-overflow: clip;
    min-width: 108px;
    /* [PHONE-LAYOUT 2026-10-22] the real phone header's 8px 10px (DataTable Th), so the header row is as tall */
    padding: 8px 10px;
  }
`;

const SkTd = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  vertical-align: middle;

  @media (max-width: 768px) {
    min-width: 108px;
    /* [PHONE-LAYOUT 2026-10-22] 10px 10px around the 12px bar = a 33px row, the height of a real one-line phone row
       (13px text in 8px 10px), so the rows do not jump when the data lands */
    padding: 10px 10px;
  }
`;

export interface SkeletonColumn {
  key?: string;
  label?: string;
  align?: 'left' | 'right' | 'center';
  headerAlign?: 'left' | 'right' | 'center';
}

interface TableSkeletonProps {
  columns: SkeletonColumn[];
  rows?: number;
  // Render an extra trailing action column (e.g. the Inventory "Delete" cell).
  actions?: boolean;
}

// Deterministic per-column bar widths so the placeholder reads like real data
// (varied lengths) yet never reflows between renders. Right-aligned numeric
// columns get a shorter, right-hugging bar.
const BAR_WIDTHS = ['82%', '64%', '90%', '55%', '74%', '48%', '88%', '60%', '70%', '52%'];

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ columns, rows = 8, actions }) => (
  <SkWrap aria-busy="true" aria-label="Loading">
    <SkTable>
      <thead>
        <tr>
          {columns.map((col, i) => (
            <SkTh key={col.key ?? i} $align={col.headerAlign ?? col.align}>
              {col.label}
            </SkTh>
          ))}
          {actions && <SkTh $align="center" />}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            {columns.map((col, c) => {
              const align = col.align;
              // Slight width jitter per row keeps long runs from looking like a
              // printed grid, while staying deterministic for a given cell.
              const base = BAR_WIDTHS[(c + r) % BAR_WIDTHS.length];
              const w = align === 'right' ? '52%' : align === 'center' ? '60%' : base;
              return (
                <SkTd key={col.key ?? c}>
                  <SkeletonBox $w={w} $align={align} />
                </SkTd>
              );
            })}
            {actions && (
              <SkTd>
                <SkeletonBox $w="18px" $h="18px" $radius="5px" $align="center" />
              </SkTd>
            )}
          </tr>
        ))}
      </tbody>
    </SkTable>
  </SkWrap>
);

/* ─────────────────────────  KPI skeleton  ───────────────────────── */

const KpiWrap = styled.div`
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px;
  background: ${(p) =>
    p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(22,25,23,0.015)'};
  animation: skFade 0.18s ease both;
  @keyframes skFade {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

const KpiGridSk = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$cols ? `repeat(${p.$cols}, minmax(0, 1fr))` : 'repeat(auto-fill, minmax(150px, 1fr))'};
  gap: 12px;
`;

const KpiCellSk = styled.div`
  padding: 12px 14px;
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 12px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff')};
`;

interface KpiSkeletonProps {
  count?: number;
  // Fix the number of columns to match a known KPI grid; omit for responsive.
  cols?: number;
  // Render the small title row above the grid.
  title?: boolean;
  // Taller cards (extra sub-text lines) to match KPI cards that carry a
  // sub/description beneath the value — keeps the height from jumping when the
  // real cards render.
  tall?: boolean;
}

export const KpiSkeleton: React.FC<KpiSkeletonProps> = ({ count = 8, cols, title = true, tall }) => (
  <KpiWrap aria-busy="true" aria-label="Loading">
    {title && <SkeletonBox $w="160px" $h="13px" $mt="2px" />}
    <KpiGridSk $cols={cols} style={{ marginTop: title ? 12 : 0 }}>
      {Array.from({ length: count }).map((_, i) => (
        <KpiCellSk key={i}>
          <SkeletonBox $w="70%" $h="9px" />
          <SkeletonBox $w="55%" $h="17px" $mt="10px" />
          {tall && (
            <>
              <SkeletonBox $w="80%" $h="8px" $mt="12px" />
              <SkeletonBox $w="95%" $h="8px" $mt="6px" />
            </>
          )}
        </KpiCellSk>
      ))}
    </KpiGridSk>
  </KpiWrap>
);

/* ─────────────────────  [MOTION-A 2026-08-20] Skeleton → content  ─────────────────────

   The handoff from skeleton to real data was a hard swap: the skeleton vanished and the
   table/cards appeared in the same frame. `Reveal` gives the arriving content a short
   fade + 4px rise so the data settles in instead of snapping.

   Deliberately a MOUNT animation, not a crossfade of both layers: rendering the skeleton
   and the content together would double the page height for a few frames and shift
   everything below. The skeleton leaving instantly while the content eases in reads as one
   smooth handoff and cannot move the layout.

   `$plain` drops the rise for containers where a transform would be unsafe — a transformed
   ancestor becomes a containing block, which can break `position: sticky` table headers
   (see DataTable's [STICKY-HEAD] wrapper) and glass/backdrop-filter surfaces.

   Cheap: one composited property pair on ONE element, 200ms, no per-row animation — the
   large tables in this app must never animate row by row.
*/
const RevealBox = styled.div<{ $plain?: boolean }>`
  animation: ${(p) => (p.$plain ? 'skReveal' : 'skRevealRise')} 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
  /* [FLUID-8 2026-10-12, user] from 40 % (was 0): the arriving data is readable from its first frame */
  @keyframes skReveal {
    from { opacity: 0.4; }
    to   { opacity: 1; }
  }
  @keyframes skRevealRise {
    from { opacity: 0.4; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* [FLUID-4/5 2026-10-12, user] `$rows` (opt-in): every row on screen in the tables inside also rises in, top to bottom,
   as the data lands (components/Fluid.tsx landRows — rows out of sight stay still). A component now (it was a plain
   styled div) only to hold the ref the wave needs; every other prop goes to the same box as before. */
type RevealProps = React.HTMLAttributes<HTMLDivElement> & { $plain?: boolean; $rows?: boolean };
export const Reveal = React.forwardRef<HTMLDivElement, RevealProps>(({ $rows, ...rest }, ref) => {
  const own = useRef<HTMLDivElement | null>(null);
  const setRef = useCallback((el: HTMLDivElement | null) => {
    own.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  }, [ref]);
  useRowsLand(own, $rows ? 'land' : null);
  return <RevealBox ref={setRef} {...rest} />;
});
Reveal.displayName = 'Reveal';

export default SkeletonBox;
